import type { DailyLog, ProjectTrack } from '../../api/types';

/**
 * Pure series computation for the per-track progress chart (W2) — no React,
 * no MUI. The chart view renders whatever this produces; vitest covers the
 * rules here directly.
 *
 *  - Daily bars: sum of progressIncrement per reportedDate.
 *  - Cumulative line: running total of the same.
 *  - Target line: linear ramp 0 → totalUnits across the track's target span —
 *    track.plannedStart/plannedEnd when both set (W3), else the min/max of the
 *    items' resolved (scheduled ?? planned) dates, else no target line.
 *  - Blocked markers: 0 on dates where any log is blocked, null elsewhere.
 */

export interface ProgressSeries {
  /** ISO dates, ascending, one per calendar day across the span. */
  dates: string[];
  daily: number[];
  cumulative: number[];
  /** Linear ramp to totalUnits, or null when the track has no target span. */
  target: number[] | null;
  /** 0 on blocked days, null elsewhere (drives red markers at the axis). */
  blocked: (number | null)[];
  empty: boolean;
}

const MS_PER_DAY = 86_400_000;

function toDay(iso: string): number {
  return Math.floor(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) / MS_PER_DAY);
}

function toIso(day: number): string {
  return new Date(day * MS_PER_DAY).toISOString().slice(0, 10);
}

/** The track's target span: track planned dates first, else the items' resolved span. */
export function targetSpan(track: ProjectTrack): { start: string; end: string } | null {
  if (track.plannedStart && track.plannedEnd) {
    return { start: track.plannedStart, end: track.plannedEnd };
  }
  const starts: string[] = [];
  const ends: string[] = [];
  for (const item of track.items ?? []) {
    const s = item.scheduledStart ?? item.plannedStart;
    const e = item.scheduledEnd ?? item.plannedEnd;
    if (s && e) {
      starts.push(s);
      ends.push(e);
    }
  }
  if (!starts.length) return null;
  // ISO dates compare lexically == chronologically.
  return { start: starts.reduce((a, b) => (a < b ? a : b)), end: ends.reduce((a, b) => (a > b ? a : b)) };
}

export function buildProgressSeries(
  logs: DailyLog[],
  track: ProjectTrack,
  todayIso: string,
): ProgressSeries {
  const span = targetSpan(track);
  const logDays = logs.map((l) => toDay(l.reportedDate));

  if (!logs.length && !span) {
    return { dates: [], daily: [], cumulative: [], target: null, blocked: [], empty: true };
  }

  const candidatesStart: number[] = [...logDays];
  const candidatesEnd: number[] = [...logDays, toDay(todayIso)];
  if (span) {
    candidatesStart.push(toDay(span.start));
    candidatesEnd.push(toDay(span.end));
  }
  const startDay = Math.min(...candidatesStart);
  const endDay = Math.max(...candidatesEnd);

  const dailyByDay = new Map<number, number>();
  const blockedDays = new Set<number>();
  for (const log of logs) {
    const d = toDay(log.reportedDate);
    if (log.isBlocked) blockedDays.add(d);
    else dailyByDay.set(d, (dailyByDay.get(d) ?? 0) + log.progressIncrement);
  }

  const dates: string[] = [];
  const daily: number[] = [];
  const cumulative: number[] = [];
  const blocked: (number | null)[] = [];
  const target: number[] | null = span ? [] : null;

  const spanStartDay = span ? toDay(span.start) : 0;
  const spanEndDay = span ? toDay(span.end) : 0;
  const spanLen = span ? spanEndDay - spanStartDay + 1 : 1;

  let running = 0;
  for (let d = startDay; d <= endDay; d++) {
    dates.push(toIso(d));
    const inc = dailyByDay.get(d) ?? 0;
    running += inc;
    daily.push(inc);
    cumulative.push(running);
    blocked.push(blockedDays.has(d) ? 0 : null);
    if (target) {
      const frac = Math.min(1, Math.max(0, (d - spanStartDay + 1) / spanLen));
      target.push(Math.round(track.totalUnits * frac));
    }
  }

  return { dates, daily, cumulative, target, blocked, empty: false };
}
